import styled from "@emotion/styled";
import { spacing } from "@atlas-int/tokens";
import { ProductClass, ProductLevel, TradeDirection, LocationDetailLevel } from "@atlas-int/data/types";
import { getColorMap } from "@atlas-int/viz";
import { getTradeDirectionSelector, servicesPartnersGroupId, ColorBy, UIView } from "@atlas-int/data";
import type { ProductMetadataBundle } from "./types";
import { memo, useCallback, useContext, useEffect, useMemo } from "react";
import complexityColorScale from "./complexityColorScale";
import { ChartContainerSizeContext } from "@atlas-int/viz/shared";
import { useDownload } from "@atlas-int/viz";
import { TreemapCells, TreemapLabels } from "./components";
import {
  useTreemapDataTransformation,
  useTreemapHierarchy,
  useFilteredLeaves,
  useDownloadData,
} from "./hooks";
import type { OnHoverParams } from "./hooks";
import { TreemapQueryVariables } from "./types";
import type { MetadataFetchState, LocationMetadataPayload, ProductComplexityPayload } from "@atlas-int/data/types";
import type { HierarchyRectangularNode } from "d3-hierarchy";
import type { TradeDataItem, EnrichedTradeDataItem } from "@atlas-int/data/types";


const Root = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  margin: ${spacing[0]};
  padding: ${spacing[0]};
  display: flex;
  flex-direction: row;
`;

const SvgAndLabelsContainer = styled.div`
  position: relative;
  margin: ${spacing[0]};
  padding: ${spacing[0]};

  & svg {
    margin: ${spacing[0]};
    padding: ${spacing[0]};
  }
`;

interface ChartProps {
  inputData: { data: TradeDataItem[] };
  hiddenCategories: string[];
  handleTooltip: (params: OnHoverParams) => void;
  highlightedItem?: string;
  tradeDirection: TradeDirection;
  queryVariables: TreemapQueryVariables;
  isLoading: boolean;
  // Metadata passed from the data context
  locationMetadata: MetadataFetchState<LocationMetadataPayload>;
  productMetadata: ProductMetadataBundle;
  productComplexityMetadata: MetadataFetchState<ProductComplexityPayload>;
}

const Chart = ({
  inputData,
  hiddenCategories,
  handleTooltip,
  highlightedItem,
  tradeDirection,
  queryVariables,
  isLoading,
  locationMetadata,
  productMetadata,
  productComplexityMetadata,
}: ChartProps) => {
  const { dataRef, svgRef } = useDownload();
  const {
    tradeFlow,
    colorBy,
    view,
    exporter,
    importer,
    year: _year,
    productClass,
    productLevel,
    locationLevel,
  } = queryVariables;

  // tradeDirection is already computed as effectiveTradeDirection in index.tsx
  const tradeDirectionSelector = getTradeDirectionSelector({
    tradeDirection,
  });

  let { chartWidth, chartHeight } = useContext(ChartContainerSizeContext);

  // Don't render until the container has been measured with valid dimensions
  if (chartWidth <= 0 || chartHeight <= 0) {
    return null;
  }

  // Handler to close tooltip when clicking on SVG background
  const handleSvgClick = useCallback(
    (e: React.MouseEvent<SVGSVGElement>) => {
      // Only close tooltip if clicking directly on the SVG (background), not on child elements
      if (e.target === e.currentTarget) {
        handleTooltip({ datum: undefined });
      }
    },
    [handleTooltip],
  );

  const useColorMap = useMemo(() => {
    if (
      view === UIView.Products &&
      productLevel === ProductLevel.Product4digit &&
      colorBy === ColorBy.Complexity
    ) {
      return complexityColorScale;
    } else {
      return getColorMap({ view, productClass });
    }
  }, [view, productLevel, colorBy, productClass]);

  // Check if metadata is still loading based on view type
  const isMetadataLoading = useMemo(() => {
    if (view === UIView.Products) {
      if (productClass === ProductClass.HS92Products) {
        return productMetadata.hs92.status === "loading";
      } else if (productClass === ProductClass.HS12Products) {
        return productMetadata.hs12.status === "loading";
      } else if (productClass === ProductClass.HS22Products) {
        return productMetadata.hs22.status === "loading";
      } else if (productClass === ProductClass.SITCProducts) {
        return productMetadata.sitc.status === "loading";
      }
    } else if (view === UIView.Markets) {
      return (
        locationMetadata.status === "loading"
      );
    }
    return false;
  }, [
    view,
    productClass,
    productMetadata.hs92.status,
    productMetadata.hs12.status,
    productMetadata.hs22.status,
    productMetadata.sitc.status,
    locationMetadata.status,
  ]);

  // Transform data
  const dataTransformationResult = useTreemapDataTransformation({
    inputData,
    queryVariables,
    hiddenCategories,
    tradeDirection,
    tradeFlow,
    tradeDirectionSelector,
    locationMetadata,
    productMetadata,
    productComplexityMetadata,
  });

  // Create hierarchies
  const hierarchyResult = useTreemapHierarchy({
    dataTransformationResult,
    chartWidth,
    chartHeight,
    view,
    tradeFlow,
    tradeDirection,
    tradeDirectionSelector,
  });

  const {
    root,
    servicesRoot,
    nonServicesRoot,
    totalSum,
    locationHasServices,
    servicesProportion,
    noDataToDisplay,
    products,
  } = hierarchyResult;

  // Get filtered leaves
  const {
    filteredRootLeaves,
    filteredServicesRootLeaves,
    filteredNonServicesRootLeaves,
  } = useFilteredLeaves({
    root,
    servicesRoot,
    nonServicesRoot,
    view,
    locationHasServices,
  });

  // Effect to handle programmatic highlighting (Find in Viz)
  useEffect(() => {
    if (highlightedItem) {
      type TreemapLeaf = HierarchyRectangularNode<EnrichedTradeDataItem>;
      let leaf: TreemapLeaf | undefined = undefined;
      let isNonServices = false;

      // Determine if we are in split view mode (services/non-services separated)
      const isSplitView =
        locationHasServices &&
        (view === UIView.Products || view === UIView.Markets);

      if (isSplitView) {
        // If in split view, check filtered services/non-services leaves ONLY
        if (filteredServicesRootLeaves) {
          leaf = (filteredServicesRootLeaves as TreemapLeaf[]).find(
            (l) =>
              l.data.productId === highlightedItem ||
              l.data.id === highlightedItem,
          );
        }

        if (!leaf && filteredNonServicesRootLeaves) {
          leaf = (filteredNonServicesRootLeaves as TreemapLeaf[]).find(
            (l) =>
              l.data.productId === highlightedItem ||
              l.data.id === highlightedItem,
          );
          isNonServices = true;
        }
      } else {
        // Otherwise check main root leaves
        if (filteredRootLeaves) {
          leaf = (filteredRootLeaves as TreemapLeaf[]).find(
            (l) =>
              l.data.productId === highlightedItem ||
              l.data.id === highlightedItem,
          );
        }
      }

      if (leaf && svgRef.current) {
        const rect = svgRef.current.getBoundingClientRect();
        let xOffset = 0;
        if (isNonServices && servicesProportion) {
          xOffset = chartWidth * servicesProportion;
        }

        const cx = (leaf.x0 + leaf.x1) / 2 + xOffset;
        const cy = (leaf.y0 + leaf.y1) / 2;

        const pageX = rect.left + cx;
        const pageY = rect.top + cy;

        let color;
        let useId;

        if (view === UIView.Products) {
          if (leaf.data.topLevelParent === "root") {
            useId = leaf.data.sectorId;
          } else {
            useId = leaf.data.topLevelParent;
          }
        } else if (view === UIView.Markets) {
          if (
            leaf.id === servicesPartnersGroupId ||
            leaf.data?.id === servicesPartnersGroupId
          ) {
            useId = servicesPartnersGroupId;
          } else {
            if (locationLevel === LocationDetailLevel.region) {
              useId = leaf.data.hybrid_level_1?.id ?? leaf.data.id;
            } else if (
              locationLevel === LocationDetailLevel.subregion
            ) {
              useId = leaf.data.hybrid_level_1?.id ?? leaf.data.id;
            } else {
              useId = leaf.data.topLevelParent;
            }
          }
        }

        if (colorBy === ColorBy.Sector) {
          color = (useColorMap as Map<string, string>).get(useId as string);
        } else if (colorBy === ColorBy.Complexity) {
          color = leaf.data.pci
            ? complexityColorScale(leaf.data.pci as number)
            : "#CCCCCC";
        }

        handleTooltip({
          datum: leaf.data,
          leaf: leaf,
          setTooltipFixed: true,
          setTooltipCoords: [pageX, pageY],
          fillColor: color,
        });
      }
    }
  }, [
    highlightedItem,
    filteredRootLeaves,
    filteredServicesRootLeaves,
    filteredNonServicesRootLeaves,
    svgRef,
    chartWidth,
    servicesProportion,
    colorBy,
    useColorMap,
    view,
    locationLevel,
    handleTooltip,
    productClass,
    locationHasServices,
  ]);

  // Update download data
  useDownloadData({
    filteredServicesRootLeaves,
    filteredNonServicesRootLeaves,
    filteredRootLeaves,
    view,
    tradeDirection,
    totalSum,
    products,
    dataRef,
  });

  const contentToRender = useMemo(() => {
    // No-data and loading overlays are handled by the parent
    // (ConnectedTreemap via VisualizationContent). Return null to avoid duplicates.
    const hasNoData =
      (root === undefined && noDataToDisplay === true) ||
      (view === UIView.Products &&
        locationHasServices &&
        !filteredServicesRootLeaves?.length &&
        !filteredNonServicesRootLeaves?.length) ||
      (view === UIView.Products &&
        !locationHasServices &&
        !filteredRootLeaves?.length) ||
      (view === UIView.Markets &&
        locationHasServices &&
        !filteredServicesRootLeaves?.length &&
        !filteredNonServicesRootLeaves?.length) ||
      (view === UIView.Markets &&
        !locationHasServices &&
        !filteredRootLeaves?.length);

    if (hasNoData && !isLoading && !isMetadataLoading) {
      return null;
    }

    if (
      (view === UIView.Products && locationHasServices) ||
      (view === UIView.Markets && locationHasServices)
    ) {
      return (
        <>
          <SvgAndLabelsContainer>
            <svg
              role="img"
              aria-label="Treemap visualization"
              ref={svgRef}
              style={{ backgroundColor: "white" }}
              width={chartWidth}
              height={chartHeight}
              onClick={handleSvgClick}
            >
              <TreemapCells
                leaves={filteredServicesRootLeaves}
                view={view}
                handleTooltip={handleTooltip}
                colorBy={colorBy}
                useColorMap={useColorMap}
                highlightedItem={highlightedItem}
                locationLevel={locationLevel}
              />
              <TreemapCells
                leaves={filteredNonServicesRootLeaves}
                view={view}
                handleTooltip={handleTooltip}
                colorBy={colorBy}
                useColorMap={useColorMap}
                highlightedItem={highlightedItem}
                locationLevel={locationLevel}
                servicesWidth={
                  servicesProportion
                    ? chartWidth * servicesProportion
                    : undefined
                }
                nonServices
              />
              <TreemapLabels
                leaves={filteredServicesRootLeaves}
                exporter={exporter}
                importer={importer}
                totalSum={totalSum}
                view={view}
                colorBy={colorBy}
                locationDetailLevel={locationLevel as string}
              />
              <TreemapLabels
                leaves={filteredNonServicesRootLeaves}
                exporter={exporter}
                importer={importer}
                totalSum={totalSum}
                servicesWidth={
                  servicesProportion
                    ? chartWidth * servicesProportion
                    : undefined
                }
                nonServices
                view={view}
                colorBy={colorBy}
                locationDetailLevel={locationLevel as string}
              />
            </svg>
          </SvgAndLabelsContainer>
        </>
      );
    } else {
      // Products without services OR Markets without services
      return (
        <SvgAndLabelsContainer>
          <svg
            role="img"
            aria-label="Treemap visualization"
            ref={svgRef}
            width={chartWidth}
            height={chartHeight}
            onClick={handleSvgClick}
          >
            <TreemapCells
              leaves={filteredRootLeaves}
              view={view}
              handleTooltip={handleTooltip}
              colorBy={colorBy}
              useColorMap={useColorMap}
              highlightedItem={highlightedItem}
              locationLevel={locationLevel}
            />
            <TreemapLabels
              leaves={filteredRootLeaves}
              exporter={exporter}
              importer={importer}
              totalSum={totalSum}
              view={view}
              colorBy={colorBy}
              locationDetailLevel={locationLevel as string}
            />
          </svg>
        </SvgAndLabelsContainer>
      );
    }
  }, [
    root,
    noDataToDisplay,
    view,
    svgRef,
    chartWidth,
    servicesProportion,
    chartHeight,
    filteredServicesRootLeaves,
    filteredNonServicesRootLeaves,
    colorBy,
    useColorMap,
    highlightedItem,
    locationLevel,
    exporter,
    importer,
    totalSum,
    filteredRootLeaves,
    isLoading,
    isMetadataLoading,
    locationHasServices,
    handleTooltip,
    handleSvgClick,
  ]);

  if (contentToRender !== null) {
    return (
      <Root>
        {contentToRender}
      </Root>
    );
  } else {
    return null;
  }
};

export default memo(Chart);
